import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { sourcePath, type CashActivity, type CashFlowDto, type CashFlowGroup, type CashFlowLinesDto, type CashFlowMappingsDto, type CashIndicator, type CashIndicatorsDto } from "@delta/shared";
import { useLang, type T } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { useToast } from "../../components/Toast";
import { AccName } from "../accounting/common";
import { Body, Frame, useReport } from "./Reports";
import { amt, initialPeriod, isZero, PeriodChip, periodQuery, ReportFilters, ReportTable, when, type Filter, type Period, type RCol } from "./common";
import { indentPx } from "../../lib/indent";

// Cash flow statement, cash flow indicators and the cash flow classification (correction pass 2026-10-27, item 9;
// docs/financial-reports.md). Every figure comes from the server, which reconciles the statement to the ledger; this
// screen only lays it out. Nothing is classified unless an account type or account is mapped in the classification.

const qs = (url: string, p: Period | null, v: Record<string, string>) => {
  const q = [p ? periodQuery(p) : "", v.branchId ? `branchId=${v.branchId}` : ""].filter(Boolean).join("&");
  return url + (q ? "?" + q : "");
};
const Provisional = () => { const { t } = useLang(); return <div className="dep-note" data-testid="rp-provisional"><Icon name="info" /><span>{t("fsProvisional")}</span></div>; };
const neg = (x: string) => x.startsWith("-") && !isZero(x);
const Signed = ({ v }: { v: string }) => <span className={`num ${neg(v) ? "neg" : ""}`}>{amt(v)}</span>;

function useBranchFilter(): Filter {
  const { t, L } = useLang();
  const { branches } = useSession();
  return { k: "branchId", label: t("rpBranch"), options: branches.map((b) => [b.id, L(b)] as [string, string]) };
}

type CfLine =
  | { head: string; group: CashFlowGroup }
  | { row: CashFlowDto["sections"][number]["rows"][number]; group: CashFlowGroup }
  | { sub: string; group: CashFlowGroup; inflow: string; outflow: string; net: string }
  | { grand: string; net: string; testId: string };

/** Lines behind a row: date, entry, document, counterpart account and amount, each linked. */
function DrillDialog({ title, url, onClose }: { title: string; url: string; onClose(): void }) {
  const { t } = useLang();
  const [d, setD] = useState<CashFlowLinesDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => { api<CashFlowLinesDto>("GET", url).then(setD, (e) => setFailed(errorText(t, e))); }, [url]);
  return (
    <div className="modal-back open" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal wide" data-testid="cf-drill">
        <div className="modal-head"><div><h3>{title}</h3></div><button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label={t("close")}><Icon name="x" /></button></div>
        <div className="modal-body">
          {failed && <div className="banner bad"><Icon name="alert" /><span>{failed}</span></div>}
          {!d && !failed && <p>{t("loading")}</p>}
          {d && <div className="table-wrap"><table className="tbl rp-tbl"><thead><tr><th>{t("date")}</th><th>{t("journalId")}</th><th>{t("stDocument")}</th><th>{t("description")}</th><th>{t("accountIndex")}</th><th className="end">{t("amount")}</th></tr></thead>
            <tbody>{d.rows.map((l, i) => (
              <tr key={i}><td className="num">{when(l.entryAt)}</td><td><Link className="num" to={`/journals/show/${l.entryId}`}>{l.number ?? "–"}</Link></td>
                <td>{l.source ? <Link className="num" to={sourcePath(l.source)}>{l.source.code}</Link> : <span className="muted">–</span>}{l.source?.reversal && <> <span className="pill">{t("rpReversal")}</span></>}</td>
                <td>{l.statement}</td><td><AccName a={l.account} /></td><td className="end"><Signed v={l.amount} /></td></tr>
            ))}</tbody>
            <tfoot><tr><td colSpan={5}><b>{t("total")}</b></td><td className="end"><b><Signed v={d.total} /></b></td></tr></tfoot></table></div>}
        </div>
        <div className="modal-foot"><button type="button" className="btn" onClick={onClose}>{t("close")}</button></div>
      </div>
    </div>
  );
}

export function CashFlowReport() {
  const { t, L } = useLang();
  const { can } = useSession();
  const branch = useBranchFilter();
  const [p, setP] = useState<Period>(() => initialPeriod());
  const [v, setV] = useState<Record<string, string>>({});
  const build = useCallback((pp: Period | null, vv: Record<string, string>) => qs("/reports/cash-flow", pp, vv), []);
  const r = useReport<CashFlowDto>(build);
  const [drill, setDrill] = useState<{ title: string; url: string } | null>(null);
  useEffect(() => { void r.run(p, v); }, []);
  const d = r.data;
  const lines: CfLine[] = [];
  if (d) {
    lines.push({ grand: t("cfOpening"), net: d.opening, testId: "cf-opening" });
    for (const s of d.sections) {
      if (!s.rows.length) continue;
      lines.push({ head: t(`cf_${s.group}`), group: s.group }, ...s.rows.map((row) => ({ row, group: s.group })),
        { sub: t("cfSectionTotal", { x: t(`cf_${s.group}`) }), group: s.group, inflow: s.inflow, outflow: s.outflow, net: s.net });
    }
    lines.push({ grand: t("cfNet"), net: d.net, testId: "cf-net" }, { grand: t("cfClosing"), net: d.closing, testId: "cf-closing" });
  }
  const linesUrl = (pairs: [string, string][]) => qs("/reports/cash-flow/lines", p, v) + (p.from || p.to || v.branchId ? "&" : "?") + pairs.map(([a, b]) => `${a}=${b}`).join("&");
  const cols: RCol<CfLine>[] = [
    { label: t("name"), cell: (x) => ("row" in x ? <span className="rp-ind2"><AccName a={x.row.account} />{x.row.mappedBy && <small className="muted"> · {t(`cfMappedBy_${x.row.mappedBy}`)}</small>}</span> : <b>{"head" in x ? x.head : "sub" in x ? x.sub : x.grand}</b>),
      text: (x) => ("row" in x ? `${x.row.account.code} ${L(x.row.account)}` : "head" in x ? x.head : "sub" in x ? x.sub : x.grand) },
    { label: t("cfInflow"), end: true, cell: (x) => ("row" in x ? <Signed v={x.row.inflow} /> : "sub" in x ? <b><Signed v={x.inflow} /></b> : ""), text: (x) => ("row" in x ? amt(x.row.inflow) : "sub" in x ? amt(x.inflow) : "") },
    { label: t("cfOutflow"), end: true, cell: (x) => ("row" in x ? <Signed v={x.row.outflow} /> : "sub" in x ? <b><Signed v={x.outflow} /></b> : ""), text: (x) => ("row" in x ? amt(x.row.outflow) : "sub" in x ? amt(x.outflow) : "") },
    { label: t("cfNetCol"), end: true, cell: (x) => ("head" in x ? "" : "row" in x ? <Signed v={x.row.net} /> : <b data-testid={"grand" in x ? x.testId : `cf-net-${x.group}`}><Signed v={x.net} /></b>),
      text: (x) => ("head" in x ? "" : amt("row" in x ? x.row.net : x.net)) },
    { label: t("cfDrill"), screenOnly: true, cell: (x) => ("row" in x || "sub" in x ? (
      <button type="button" className="btn btn-sm btn-ghost" data-testid="cf-drill-btn" aria-label={t("cfDrill")} title={t("cfDrill")} onClick={() => setDrill("row" in x
        ? { title: t("cfDrillTitle", { x: `${x.row.account.code} ${L(x.row.account)}` }), url: linesUrl([["accountId", x.row.account.id], ["group", x.group]]) }
        : { title: t("cfDrillTitle", { x: t(`cf_${x.group}`) }), url: linesUrl([["group", x.group]]) })}><Icon name="search" /></button>) : ""), text: () => "" }
  ];
  const ok = d && isZero(d.difference);
  const unmapped = d?.sections.find((s) => s.group === "unmapped")?.rows.length ?? 0;
  const notes = d ? [
    ok ? t("cfReconciled", { x: amt(d.ledgerNet) }) : t("cfNotReconciled", { l: amt(d.ledgerNet), n: amt(d.net), d: amt(d.difference) }),
    ...(unmapped ? [t("cfUnmappedNote", { n: unmapped })] : []), ...(d.mixedEntries ? [t("cfMixedNote", { n: d.mixedEntries })] : []),
    d.cash.length ? t("cfCash", { x: d.cash.map((a) => `${a.code} ${L(a)}`).join(", ") }) : t("cfNoCash")
  ] : [];
  const title = t("cashFlow");
  return (
    <Frame group="repFinancial" title={title} actions={can("setting") || can("r2balanceSheet") ? <Link className="btn" to="/reports/cash-flow/classification"><Icon name="settings" />{t("cashFlowMappings")}</Link> : undefined}
      filters={<><Provisional /><ReportFilters period={p} filters={[branch]} values={v} onSearch={(pp, vv) => { setP(pp!); setV(vv); void r.run(pp, vv); }} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <>
        <div className={`banner ${ok ? "good" : "bad"} rp-check`} data-testid="cf-check"><Icon name={ok ? "check" : "alert"} /><span>{notes[0]}</span></div>
        {notes.slice(1, -1).map((n, i) => <div key={i} className="banner rp-check" data-testid="cf-note"><Icon name="info" /><span>{n}</span></div>)}
        <ReportTable title={title} file="cash-flow" cls="rp-fs" cols={cols} rows={lines} notes={notes}
          rowCls={(x) => ("head" in x ? "rp-h" : "sub" in x ? "rp-s" : "grand" in x ? "rp-g" : "")}
          chips={<><PeriodChip from={d.from} to={d.to} />{d.branch && <span className="fchip">{t("rpBranch")}: <b>{L(d.branch)}</b></span>}<span className="fchip muted">{notes[notes.length - 1]}</span></>} testId="report-table" />
      </>}</Body>
      {drill && <DrillDialog title={drill.title} url={drill.url} onClose={() => setDrill(null)} />}
    </Frame>
  );
}

const indicatorValue = (t: T, x: CashIndicator) => (x.value === null ? t("cfIndZero") : x.unit === "amount" ? amt(x.value) : x.value);

export function CashFlowIndicatorsReport() {
  const { t, L } = useLang();
  const branch = useBranchFilter();
  const [p, setP] = useState<Period>(() => initialPeriod());
  const [v, setV] = useState<Record<string, string>>({});
  const build = useCallback((pp: Period | null, vv: Record<string, string>) => qs("/reports/cash-flow-indicators", pp, vv), []);
  const r = useReport<CashIndicatorsDto>(build);
  useEffect(() => { void r.run(p, v); }, []);
  const d = r.data;
  const cols: RCol<CashIndicator>[] = [
    { label: t("name"), cell: (x) => <b>{t(`cfInd_${x.key}`)}</b>, text: (x) => t(`cfInd_${x.key}`) },
    { label: t("cfIndFormula"), cell: (x) => <span className="muted">{t(`cfIndF_${x.key}`)}</span>, text: (x) => t(`cfIndF_${x.key}`) },
    { label: t("cfIndInputs"), cell: (x) => <span className="num">{x.denominator === null ? amt(x.numerator) : `${amt(x.numerator)} ÷ ${amt(x.denominator)}`}</span>,
      text: (x) => (x.denominator === null ? amt(x.numerator) : `${amt(x.numerator)} ÷ ${amt(x.denominator)}`) },
    { label: t("cfIndValue"), end: true, cell: (x) => <b className={x.value === null ? "muted" : "num"} data-testid={`ind-${x.key}`}>{indicatorValue(t, x)}</b>, text: (x) => indicatorValue(t, x) }
  ];
  const title = t("cashFlowIndicators");
  const notes = d ? [t("cfIndProposed"), t("cfIndDays", { n: d.days }),
    t("cfIndSource", { from: d.from ?? t("pr_all"), to: d.to ?? t("pr_all"), b: d.branch ? L(d.branch) : t("allBranches") }),
    ...(d.unmapped.accounts ? [t("cfIndUnmapped", { n: d.unmapped.accounts, x: amt(d.unmapped.net) })] : [])] : [];
  return (
    <Frame group="repFinancial" title={title} filters={<><Provisional /><ReportFilters period={p} filters={[branch]} values={v} onSearch={(pp, vv) => { setP(pp!); setV(vv); void r.run(pp, vv); }} /></>}>
      <Body failed={r.failed} loading={r.loading}>{d && <>
        <div className="banner warn rp-check" data-testid="ind-proposed"><Icon name="info" /><span>{notes[0]}</span></div>
        {d.unmapped.accounts > 0 && <div className="banner rp-check" data-testid="ind-unmapped"><Icon name="info" /><span>{notes[3]}</span></div>}
        <ReportTable title={`${title} · ${t("cfIndProposedTag")}`} file="cash-flow-indicators" cols={cols} rows={d.indicators} notes={notes}
          chips={<><PeriodChip from={d.from} to={d.to} />{d.branch && <span className="fchip">{t("rpBranch")}: <b>{L(d.branch)}</b></span>}<span className="fchip">{notes[1]}</span></>} testId="report-table" />
      </>}</Body>
    </Frame>
  );
}

const ACTS: CashActivity[] = ["operating", "investing", "financing"];

/** The classification the cash flow statement uses: each account type, and each account that differs from its type. */
export function CashFlowMappingsPage() {
  const { t, L } = useLang();
  const { can } = useSession();
  const toast = useToast();
  const [d, setD] = useState<CashFlowMappingsDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);
  useEffect(() => { api<CashFlowMappingsDto>("GET", "/reports/cash-flow/mappings").then(setD, (e) => setFailed(errorText(t, e))); }, []);
  const edit = can("setting");
  const pick = (value: CashActivity | null, on: (a: CashActivity | null) => void, id: string, disabled?: boolean): ReactNode => (
    <select className="control" value={value ?? ""} disabled={!edit || disabled} onChange={(e) => on((e.target.value || null) as CashActivity | null)} data-testid={id}>
      <option value="">{t("cfMapNone")}</option>{ACTS.map((a) => <option key={a} value={a}>{t(`cf_${a}`)}</option>)}
    </select>
  );
  async function save() {
    if (!d) return;
    setBusy(true); setAlert(null);
    try {
      setD(await api<CashFlowMappingsDto>("PUT", "/reports/cash-flow/mappings", {
        types: d.types.map((x) => ({ typeId: x.type.id, activity: x.activity })), accounts: d.accounts.filter((x) => !x.cash).map((x) => ({ accountId: x.account.id, activity: x.activity }))
      }));
      toast(t("cfMapSaved"));
    } catch (e) { setAlert(errorText(t, e)); } finally { setBusy(false); }
  }
  // A group without its own activity takes the nearest group above it that has one (rule H-6), as the report does.
  const inherited = (id: string): string | null => {
    const by = new Map((d?.types ?? []).map((y) => [y.type.id, y]));
    for (let p = by.get(id)?.type.parentId ?? null; p; p = by.get(p)?.type.parentId ?? null) {
      const up = by.get(p);
      if (up?.activity) return t("cfInherited", { g: L(up.type), a: t(`cf_${up.activity}`) });
    }
    return null;
  };
  const title = t("cashFlowMappings");
  return (
    <Shell navKey="/reports">
      <PageHead crumbs={[[t("home"), "/home"], [t("reports"), "/reports"], [t("cashFlow"), "/reports/cash-flow"], [title]]} title={title} sub={t("cfMapHint")}
        actions={edit ? <button className="btn btn-primary" onClick={() => void save()} disabled={busy || !d} data-testid="cf-map-save"><Icon name="check" />{busy ? t("saving") : t("save")}</button> : undefined} />
      {failed && <section className="card"><div className="state-box error"><Icon name="alert" /><p>{failed}</p></div></section>}
      {alert && <div className="banner bad" role="alert"><Icon name="alert" /><span>{alert}</span></div>}
      {d && <>
        <section className="card" data-testid="cf-map-types"><div className="card-head"><h2>{t("cfMapType")}</h2></div>
          <div className="table-wrap"><table className="tbl"><tbody>{d.types.map((x, i) => (
            <tr key={x.type.id}><td><span style={{ paddingInlineStart: `${indentPx(x.type.depth - 1, 18)}px` }}>{x.type.code && <span className="num muted">{x.type.code} </span>}{L(x.type)}</span>
              {!x.activity && inherited(x.type.id) && <span className="muted d-block" style={{ fontSize: 12, paddingInlineStart: `${indentPx(x.type.depth - 1, 18)}px` }} data-testid={`cf-map-inherited-${i}`}>{inherited(x.type.id)}</span>}</td>
              <td style={{ width: 260 }}>{pick(x.activity, (a) => setD({ ...d, types: d.types.map((y, j) => (j === i ? { ...y, activity: a } : y)) }), `cf-map-type-${i}`)}</td></tr>
          ))}</tbody></table></div></section>
        <section className="card" style={{ marginTop: 16 }} data-testid="cf-map-accounts"><div className="card-head"><h2>{t("cfMapAccounts")}</h2></div>
          <div className="table-wrap"><table className="tbl"><tbody>{d.accounts.map((x, i) => (
            <tr key={x.account.id}><td><AccName a={x.account} />{x.cash && <span className="pill info"> {t("cfCashAccount")}</span>}</td>
              <td className="muted">{L(d.types.find((y) => y.type.id === x.account.typeId)?.type)}</td>
              <td style={{ width: 260 }}>{x.cash ? <span className="muted">–</span> : pick(x.activity, (a) => setD({ ...d, accounts: d.accounts.map((y, j) => (j === i ? { ...y, activity: a } : y)) }), `cf-map-acc-${i}`)}</td></tr>
          ))}</tbody></table></div></section>
      </>}
    </Shell>
  );
}
